import type { AccountLocale } from "@kaleo/account-localization";
import cs from "../messages/cs.json";
import de from "../messages/de.json";
import en from "../messages/en.json";
import esEs from "../messages/es-ES.json";
import es419 from "../messages/es-419.json";
import frFr from "../messages/fr-FR.json";
import frCa from "../messages/fr-CA.json";
import ko from "../messages/ko.json";
import nl from "../messages/nl.json";
import no from "../messages/no.json";
import ptBr from "../messages/pt-BR.json";
import ru from "../messages/ru.json";
import sv from "../messages/sv.json";
import vi from "../messages/vi.json";
import zhHans from "../messages/zh-Hans.json";
import zhHant from "../messages/zh-Hant.json";

export type DocsMessageKey = keyof typeof en;
export type DocsMessages = Record<DocsMessageKey, string>;

const catalogs: Record<AccountLocale, DocsMessages> = {
  en,
  "es-ES": esEs,
  "es-419": es419,
  ko,
  ru,
  "pt-BR": ptBr,
  "fr-FR": frFr,
  "fr-CA": frCa,
  "zh-Hans": zhHans,
  "zh-Hant": zhHant,
  de,
  nl,
  sv,
  cs,
  no,
  vi,
};

type ViteEnvironment = {
  DEV?: boolean;
  PROD?: boolean;
  ZUDOKU_PUBLIC_APP_ORIGIN?: string;
  ZUDOKU_PUBLIC_WORKER_ORIGIN?: string;
};

const PRODUCTION_APP_ORIGIN = "https://app.withkaleo.com";
const DEVELOPMENT_APP_ORIGIN = "http://localhost:8080";
const PRODUCTION_WORKER_ORIGIN = "https://babel.withkaleo.com";

function resolveHttpOrigin(value: string, variableName: string): string {
  try {
    const url = new URL(value);
    if (
      (url.protocol !== "http:" && url.protocol !== "https:") ||
      url.username ||
      url.password ||
      url.pathname !== "/" ||
      url.search ||
      url.hash
    ) {
      throw new Error("not_an_origin");
    }
    return url.origin;
  } catch {
    throw new Error(
      `${variableName} must be an absolute HTTP(S) origin`,
    );
  }
}

export function resolveDocsAppOrigin(
  environment: ViteEnvironment | undefined,
): string {
  if (environment?.PROD === true || environment?.DEV !== true) {
    return PRODUCTION_APP_ORIGIN;
  }

  const configured = environment.ZUDOKU_PUBLIC_APP_ORIGIN?.trim();
  if (!configured) return DEVELOPMENT_APP_ORIGIN;

  return resolveHttpOrigin(configured, "ZUDOKU_PUBLIC_APP_ORIGIN");
}

export function resolveDocsWorkerOrigin(
  environment: ViteEnvironment | undefined,
): string {
  if (environment?.PROD === true || environment?.DEV !== true) {
    return PRODUCTION_WORKER_ORIGIN;
  }

  const configured = environment.ZUDOKU_PUBLIC_WORKER_ORIGIN?.trim();
  if (!configured) return PRODUCTION_WORKER_ORIGIN;

  return resolveHttpOrigin(configured, "ZUDOKU_PUBLIC_WORKER_ORIGIN");
}

const viteEnvironment = (
  import.meta as ImportMeta & { env?: ViteEnvironment }
).env;

export const APP_ORIGIN = resolveDocsAppOrigin(viteEnvironment);
export const WORKER_ORIGIN = resolveDocsWorkerOrigin(viteEnvironment);

export const DOCS_ORIGIN = "https://docs.withkaleo.com";
export const IS_PRODUCTION_DOCS = viteEnvironment?.PROD === true;

export function getDocsMessages(locale: AccountLocale): DocsMessages {
  return catalogs[locale];
}

export function formatDocsMessage(
  messages: DocsMessages,
  key: DocsMessageKey,
  values: Readonly<Record<string, string>>,
): string {
  return messages[key].replace(
    /\{([A-Za-z][A-Za-z0-9]*)\}/g,
    (placeholder, name: string) => values[name] ?? placeholder,
  );
}

export function getLocalizedLanguageName(
  languageCode: string,
  locale: AccountLocale,
  fallback: string,
): string {
  // CLDR names omit private-use distinctions and would mislabel the catalog entry.
  if (languageCode.includes("-x-")) return fallback;

  try {
    const name = new Intl.DisplayNames([locale], { type: "language" }).of(
      languageCode,
    );
    return name && name !== languageCode ? name : fallback;
  } catch {
    return fallback;
  }
}
